import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { apiService, BillingCycle } from './api';
import { FEATURE_FLAGS } from '../config/api.config';

/** Paying is possible on web and Android; on iOS only when the live-update
 * flag turns it on. Free plans and trials never need this. */
export const canPayInApp = (): boolean => Platform.OS !== 'ios' || FEATURE_FLAGS.IOS_BILLING_ENABLED;

const APP_RETURN_URL = 'dislix://billing-return';

/**
 * Opens Kashier's hosted page for `planId`/`cycle`.
 *  - web: full-page redirect; the browser comes back to /billing/return.
 *  - app: in-app browser session that closes on dislix://billing-return.
 * Resolves with the paymentId to check (app), or null after a web redirect.
 * The result is never trusted from the browser — BillingReturnScreen asks
 * the API, which verifies with Kashier.
 */
export async function startCheckout(planId: string, billingCycle: BillingCycle, language: 'en' | 'ar'): Promise<{ paymentId: string; dismissed?: boolean } | null> {
  const isWeb = Platform.OS === 'web';
  const response = await apiService.createBillingCheckout({ planId, billingCycle, language, client: isWeb ? 'web' : 'app' });
  if (response.statusCode !== 1000 || !response.data?.checkoutUrl) {
    throw new Error(response.errors?.[0]?.message || 'Could not start the payment. Please try again.');
  }
  const { paymentId, checkoutUrl } = response.data;

  if (isWeb) {
    try { window.sessionStorage.setItem('dislix.pendingPaymentId', paymentId); } catch { /* optional */ }
    window.location.href = checkoutUrl;
    return null;
  }

  const result = await WebBrowser.openAuthSessionAsync(checkoutUrl, APP_RETURN_URL);
  return { paymentId, dismissed: result.type !== 'success' };
}

/** Same as startCheckout, for add-ons bought mid-period (charged for the days left). */
export async function startAddonCheckout(items: Array<{ code: string; quantity: number }>, language: 'en' | 'ar'): Promise<{ paymentId: string; dismissed?: boolean } | null> {
  const isWeb = Platform.OS === 'web';
  const response = await apiService.createAddonCheckout({ items, language, client: isWeb ? 'web' : 'app' });
  if (response.statusCode !== 1000 || !response.data?.checkoutUrl) {
    throw new Error(response.errors?.[0]?.message || 'Could not start the payment. Please try again.');
  }
  const { paymentId, checkoutUrl } = response.data;
  if (isWeb) {
    try { window.sessionStorage.setItem('dislix.pendingPaymentId', paymentId); } catch { /* optional */ }
    window.location.href = checkoutUrl;
    return null;
  }
  const result = await WebBrowser.openAuthSessionAsync(checkoutUrl, APP_RETURN_URL);
  return { paymentId, dismissed: result.type !== 'success' };
}
